// src/context/ThemeContext.js

import React, { createContext, useState, useMemo, useContext } from 'react';
import { createTheme, ThemeProvider as MUIThemeProvider } from '@mui/material/styles';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
    // Get the stored theme from localStorage, or default to 'dark'
    const [mode, setMode] = useState(localStorage.getItem('themeMode') || 'dark');

    const toggleTheme = () => {
        setMode((prevMode) => {
            const newMode = prevMode === 'light' ? 'dark' : 'light';
            // Save the new theme choice to localStorage
            localStorage.setItem('themeMode', newMode);
            return newMode;
        });
    };

    // createTheme re-calculates the theme object whenever the mode changes
    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    ...(mode === 'light'
                        ? {
                            primary: { main: '#1d4ed8' },
                            secondary: { main: '#0ea5e9' },
                            success: { main: '#16a34a' },
                            warning: { main: '#f59e0b' },
                            error: { main: '#dc2626' },
                            background: { default: '#f5f7fb', paper: '#ffffff' },
                            text: { primary: '#0f172a', secondary: '#475569' },
                            divider: 'rgba(15, 23, 42, 0.12)',
                          }
                        : {
                            primary: { main: '#38bdf8' },
                            secondary: { main: '#22d3ee' },
                            success: { main: '#22c55e' },
                            warning: { main: '#f59e0b' },
                            error: { main: '#f97316' },
                            background: { default: '#0b0f14', paper: '#111827' },
                            text: { primary: '#e5e7eb', secondary: '#94a3b8' },
                            divider: 'rgba(148, 163, 184, 0.2)',
                          }),
                },
                shape: {
                    borderRadius: 14,
                },
                typography: {
                    fontFamily: '"Manrope", "Segoe UI", "Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
                    h1: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700, letterSpacing: -0.6 },
                    h2: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700, letterSpacing: -0.4 },
                    h3: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700, letterSpacing: -0.2 },
                    h4: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
                    h5: { fontWeight: 600 },
                    h6: { fontWeight: 600 },
                    subtitle1: { fontWeight: 600 },
                    button: { fontWeight: 600 },
                },
                components: {
                    MuiCssBaseline: {
                        styleOverrides: {
                            body: {
                                minHeight: '100vh',
                                backgroundColor: mode === 'dark' ? '#0b0f14' : '#f5f7fb',
                                backgroundImage:
                                    mode === 'dark'
                                        ? 'radial-gradient(1200px 600px at 20% -10%, rgba(56, 189, 248, 0.15), transparent), radial-gradient(900px 500px at 90% 10%, rgba(34, 211, 238, 0.12), transparent)'
                                        : 'radial-gradient(1100px 600px at 10% -10%, rgba(29, 78, 216, 0.15), transparent), radial-gradient(900px 500px at 90% 0%, rgba(14, 165, 233, 0.12), transparent)',
                                color: mode === 'dark' ? '#e5e7eb' : '#0f172a',
                            },
                        },
                    },
                    MuiPaper: {
                        styleOverrides: {
                            root: {
                                backgroundImage: 'none',
                                border: mode === 'dark' ? '1px solid rgba(148, 163, 184, 0.14)' : '1px solid rgba(15, 23, 42, 0.08)',
                            },
                        },
                    },
                    MuiCard: {
                        styleOverrides: {
                            root: {
                                backgroundImage: 'none',
                                border: mode === 'dark' ? '1px solid rgba(148, 163, 184, 0.16)' : '1px solid rgba(15, 23, 42, 0.08)',
                                boxShadow: mode === 'dark' ? '0 20px 40px rgba(2, 6, 23, 0.35)' : '0 12px 24px rgba(15, 23, 42, 0.08)',
                            },
                        },
                    },
                    MuiButton: {
                        styleOverrides: {
                            root: {
                                textTransform: 'none',
                                borderRadius: 10,
                                fontWeight: 600,
                            },
                        },
                    },
                    MuiTabs: {
                        styleOverrides: {
                            root: {
                                minHeight: 42,
                            },
                            indicator: {
                                height: 3,
                                borderRadius: 999,
                            },
                        },
                    },
                    MuiTab: {
                        styleOverrides: {
                            root: ({ theme }) => ({
                                minHeight: 42,
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 10,
                                '&.Mui-selected': {
                                    color: theme.palette.primary.main,
                                    backgroundColor:
                                        theme.palette.mode === 'dark'
                                            ? 'rgba(56, 189, 248, 0.12)'
                                            : 'rgba(29, 78, 216, 0.08)',
                                },
                            }),
                        },
                    },
                    MuiTableCell: {
                        styleOverrides: {
                            head: {
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                fontSize: '0.72rem',
                            },
                        },
                    },
                    MuiTableRow: {
                        styleOverrides: {
                            root: {
                                transition: 'background-color 0.2s ease',
                                '&:hover': {
                                    backgroundColor: mode === 'dark' ? 'rgba(56, 189, 248, 0.08)' : 'rgba(29, 78, 216, 0.06)',
                                },
                            },
                        },
                    },
                    MuiOutlinedInput: {
                        styleOverrides: {
                            root: {
                                backgroundColor: mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                            },
                        },
                    },
                    MuiToggleButton: {
                        styleOverrides: {
                            root: {
                                borderRadius: 10,
                                textTransform: 'none',
                                fontWeight: 600,
                            },
                        },
                    },
                    MuiDialog: {
                        styleOverrides: {
                            paper: {
                                backgroundImage: 'none',
                            },
                        },
                    },
                },
            }),
        [mode]
    );

    return (
        <ThemeContext.Provider value={{ mode, toggleTheme }}>
            <MUIThemeProvider theme={theme}>{children}</MUIThemeProvider>
        </ThemeContext.Provider>
    );
};

// Custom hook to use our theme context easily in any component
export const useThemeContext = () => useContext(ThemeContext);
